import {
  CARD_LOAD_MAX_AMOUNT_MINOR,
  WALLET_STATUS,
  WALLET_SUPPORT_PHONE_NUMBERS,
} from 'constants/wallet';
import { formatCurrencyAmount, getCurrencyCode } from 'utils/currencyUtil';

// The low-balance-alert widget shows whole-currency amounts keyed by the corporation country:
// "£1,000" / "1 000 €", with decimals only when the amount has them.
export const formatWalletAmount = (country, value) =>
  formatCurrencyAmount(value, getCurrencyCode(country), { wholeUnits: true });

// Card top-up amounts are in the wallet's own currency and minor units: whole amounts read as
// "£1,000", anything with pence as "£12.50".
export const formatCardLoadAmountLabel = (amountMinor, currency) => {
  if (!Number.isInteger(amountMinor)) {
    return '';
  }
  return formatCurrencyAmount(amountMinor / 100, currency, { wholeUnits: true });
};

export const getWalletSupportPhoneNumber = country =>
  WALLET_SUPPORT_PHONE_NUMBERS[country] || WALLET_SUPPORT_PHONE_NUMBERS.default;

// Frozen and closed wallets share one read-only dashboard: greyed stats, no editing, no top-ups.
export const isWalletInactive = status =>
  status === WALLET_STATUS.frozen || status === WALLET_STATUS.closed;

const AVAILABLE_BALANCE_KEY_PREFIX = 'corporateAccounts.wallet.dashboard.availableBalance';

// Which line sits under the balance: the alert threshold while active, otherwise the status line —
// with the date the status changed when the wallet service gave us one.
export const getAvailableBalanceSubLineKey = (status, hasStatusDate) => {
  if (status === WALLET_STATUS.frozen) {
    return `${AVAILABLE_BALANCE_KEY_PREFIX}.${hasStatusDate ? 'frozenLine' : 'frozenLineNoDate'}`;
  }
  if (status === WALLET_STATUS.closed) {
    return `${AVAILABLE_BALANCE_KEY_PREFIX}.${hasStatusDate ? 'closedLine' : 'closedLineNoDate'}`;
  }
  return `${AVAILABLE_BALANCE_KEY_PREFIX}.alertLine`;
};

const readFileAsArrayBuffer = file =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(file);
  });

// The wire-loads API takes a sha256 of the proof file to verify it server-side.
export const getFileSha256Hex = async file => {
  const buffer = await readFileAsArrayBuffer(file);
  const hashBuffer = await window.crypto.subtle.digest('SHA-256', buffer);
  return Array.from(new Uint8Array(hashBuffer))
    .map(byte => byte.toString(16).padStart(2, '0'))
    .join('');
};

// --- Amount inputs -------------------------------------------------------------------------------

const AMOUNT_ALLOWED_CONTROL_KEYS = [
  'Backspace',
  'Delete',
  'ArrowLeft',
  'ArrowRight',
  'Tab',
  'Home',
  'End',
];

// Text inputs (antd's InputNumber included) don't stop a user from typing letters, or 'e'/'-' for
// scientific/negative notation — only digits and a single decimal point should get through.
export const handleAmountKeyDown = event => {
  if (event.ctrlKey || event.metaKey || AMOUNT_ALLOWED_CONTROL_KEYS.includes(event.key)) {
    return;
  }

  const isDigit = /^[0-9]$/.test(event.key);
  const isFirstDecimalPoint = event.key === '.' && !event.currentTarget.value.includes('.');

  if (!isDigit && !isFirstDecimalPoint) {
    event.preventDefault();
  }
};

export const handleAmountPaste = event => {
  const pastedText = event.clipboardData.getData('text');

  if (!/^[0-9]*\.?[0-9]*$/.test(pastedText)) {
    event.preventDefault();
  }
};

// Keydown/paste guards can be bypassed (drag-and-drop, IME, autofill), so a controlled amount
// input also sanitises what actually lands in it: digits, one decimal point, at most 2 decimals.
export const sanitizeAmountInput = value => {
  const cleaned = String(value ?? '').replace(/[^0-9.]/g, '');
  const [whole = '', ...rest] = cleaned.split('.');
  if (rest.length === 0) {
    return whole;
  }
  return `${whole}.${rest.join('').slice(0, 2)}`;
};

export const isValidCardLoadAmount = amountMinor =>
  Number.isInteger(amountMinor) && amountMinor > 0 && amountMinor <= CARD_LOAD_MAX_AMOUNT_MINOR;

// --- Card load modal -----------------------------------------------------------------------------

export const CARD_LOAD_STEPS = {
  confirm: 'confirm',
  submitting: 'submitting',
  processing: 'processing',
  sca: 'sca',
  done: 'done',
};

// Which view the confirm-card-payment modal shows, derived from the card-load hook's state.
export const getCardLoadStep = ({ isSubmitting, isPolling, sca, isSuccess }) => {
  if (isSuccess) return CARD_LOAD_STEPS.done;
  if (isSubmitting) return CARD_LOAD_STEPS.submitting;
  if (isPolling && sca) return CARD_LOAD_STEPS.sca;
  if (isPolling) return CARD_LOAD_STEPS.processing;
  return CARD_LOAD_STEPS.confirm;
};
